/**
 * Calendar dates (`YYYY-MM-DD`) as the client typed them. `new Date("2026-11-02")`
 * parses as midnight UTC, which renders as November 1 anywhere west of
 * Greenwich, so a bare date is read as a local date instead.
 */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// How the server stores a date with no time: midnight UTC.
const MIDNIGHT_UTC_RE =
  /^(\d{4})-(\d{2})-(\d{2})T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)$/;

/** A calendar date with no time of day: `YYYY-MM-DD` or a midnight-UTC stamp. */
export const isCalendarDate = (value: unknown): boolean =>
  typeof value === "string" &&
  (DATE_ONLY_RE.test(value.trim()) || MIDNIGHT_UTC_RE.test(value.trim()));

/**
 * Today in the browser's zone as `YYYY-MM-DD`. Not `toISOString().slice(0, 10)`,
 * which is the UTC date and runs a day ahead on US evenings.
 */
export const todayDateOnly = (now: Date = new Date()): string => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** A local `Date` for a calendar date or timestamp string; null when unreadable. */
export const parseDateOnly = (value: unknown): Date | null => {
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw) return null;
  const match = DATE_ONLY_RE.exec(raw) ?? MIDNIGHT_UTC_RE.exec(raw);
  if (match) {
    const [, y, m, d] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.getMonth() === Number(m) - 1 ? date : null;
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "November 2, 2026" (or the given style); the raw string when it isn't a date. */
export const formatDateOnly = (
  value: unknown,
  options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric"
  }
): string => {
  const date = parseDateOnly(value);
  if (!date) return typeof value === "string" ? value : "";
  return date.toLocaleDateString(undefined, options);
};
